let rootReducerRegistry = null;
let baseApiRegistry = null;
// Store instance for accessing state (e.g., in fetchAuthWrapper)
let storeRegistry = null;
// Callbacks for features that want to inject when store is ready
const pendingSliceInjections = [];
// Callbacks for entities that want to inject API endpoints when store is ready
const pendingApiInjections = [];
/**
 * Register the app's rootReducer and baseApi in the global registry.
 * This allows features and widgets to auto-inject slices and API endpoints.
 *
 * Should be called once in each app's store.ts after creating the store.
 *
 * @param reducer - The app's rootReducer from combineSlices()
 * @param api - The app's baseApiAuthenticated instance
 *
 * @example
 * ```typescript
 * import { registerStore } from '@starjack/api/storeRegistry';
 *
 * export const rootReducer = combineSlices(...);
 * export const store = configureStore({ reducer: finalReducer, ... });
 *
 * registerStore(rootReducer, baseApiAuthenticated);
 * ```
 */
export const registerStore = (reducer, api, store) => {
    rootReducerRegistry = reducer;
    // Type assertion is safe because TApi extends the required structure
    // We cast to BaseApi for storage, but the function accepts any compatible type
    baseApiRegistry = api;
    if (store) {
        storeRegistry = store;
    }
    // Trigger all pending slice injections
    pendingSliceInjections.forEach((inject) => inject(reducer));
    pendingSliceInjections.length = 0; // Clear the array
    // Trigger all pending API endpoint injections
    pendingApiInjections.forEach((inject) => inject(baseApiRegistry));
    pendingApiInjections.length = 0; // Clear the array
};
/**
 * Get the registered store instance from the global registry.
 * Used by utilities like fetchAuthWrapper to access state.
 *
 * @throws {Error} If store hasn't been registered yet
 * @returns The registered store instance
 */
export const getStore = () => {
    if (!storeRegistry) {
        throw new Error('Store not registered. Call registerStore() with store instance in your store.ts before using fetchAuthWrapper.');
    }
    return storeRegistry;
};
/**
 * Get the registered rootReducer from the global registry.
 * Used by features to auto-inject slices.
 *
 * @throws {Error} If store hasn't been registered yet
 * @returns The registered rootReducer
 */
export const getRootReducer = () => {
    if (!rootReducerRegistry) {
        throw new Error('Store not registered. Call registerStore() in your store.ts before importing features.');
    }
    return rootReducerRegistry;
};
/**
 * Get the registered baseApi from the global registry.
 * Used by widgets to auto-inject API endpoints.
 *
 * @throws {Error} If baseApi hasn't been registered yet
 * @returns The registered baseApi
 */
export const getBaseApi = () => {
    if (!baseApiRegistry) {
        throw new Error('Base API not registered. Call registerStore() in your store.ts before importing widgets.');
    }
    return baseApiRegistry;
};
/**
 * Check if the store has been registered.
 * Useful for lazy initialization patterns.
 *
 * @returns true if store is registered, false otherwise
 */
export const isStoreRegistered = () => {
    return rootReducerRegistry !== null && baseApiRegistry !== null;
};
/**
 * Register a callback to inject a slice when the store is ready.
 * If the store is already registered, the callback is executed immediately.
 * Otherwise, it will be executed when registerStore() is called.
 *
 * @param injectCallback - Function that takes rootReducer and injects the slice
 */
export const registerSliceInjection = (injectCallback) => {
    if (rootReducerRegistry) {
        // Store is already registered, inject immediately
        injectCallback(rootReducerRegistry);
    }
    else {
        // Store not ready yet, queue for later
        pendingSliceInjections.push(injectCallback);
    }
};
/**
 * Register a callback to inject API endpoints when the store is ready.
 * If the store is already registered, the callback is executed immediately.
 * Otherwise, it will be executed when registerStore() is called.
 *
 * @param injectCallback - Function that takes baseApi and injects endpoints
 */
export const registerApiInjection = (injectCallback) => {
    if (baseApiRegistry) {
        // Store is already registered, inject immediately
        injectCallback(baseApiRegistry);
    }
    else {
        // Store not ready yet, queue for later
        pendingApiInjections.push(injectCallback);
    }
};
